import { getRole, getEntityIdForRole } from "../auth/roleService";
import { ROLES } from "../config/roles";
import {
  getSellerDashboard,
  getBuyerDashboard,
  getFreightDashboard,
  getAdminDashboard
} from "../utils/api";

/**
 * One place to map role → API + how to turn each backend’s response into UI props.
 * When an endpoint’s payload or fields differ, only adjust the matching entry below.
 */

function normalizeSeller(res) {
  const d = res?.data ?? {};
  return {
    statItems: [
      { title: "Offers", value: Number(d.offers) || 0, icon: "bi-box" },
      { title: "IRM", value: Number(d.irm) || 0, icon: "bi-inbox" },
      { title: "Calls", value: Number(d.calls) || 0, icon: "bi-telephone" },
      { title: "WhatsApp", value: Number(d.whatsapp) || 0, icon: "bi-whatsapp" }
    ],
    inventory: Array.isArray(d.inventory) ? d.inventory : [],
    raw: d
  };
}

function normalizeBuyer(res) {
  const d = res?.data ?? {};
  return {
    statItems: [
      {
        title: "Orders",
        value: Number(d.orders ?? d.orders_count ?? d.active_orders) || 0,
        icon: "bi-bag-check"
      },
      {
        title: "Quotes",
        value: Number(d.quotes ?? d.pending_quotes) || 0,
        icon: "bi-file-earmark-text"
      },
      {
        title: "IRM",
        value: Number(d.irm ?? d.instant_requests) || 0,
        icon: "bi-lightning"
      },
      {
        title: "Saved",
        value: Number(d.saved_items ?? d.watchlist) || 0,
        icon: "bi-bookmark"
      }
    ],
    inventory: Array.isArray(d.inventory) ? d.inventory : [],
    raw: d
  };
}

function normalizeFreight(res) {
  const d = res?.data ?? {};
  return {
    statItems: [
      {
        title: "Active",
        value: Number(d.active_shipments ?? d.active) || 0,
        icon: "bi-truck"
      },
      {
        title: "Pending",
        value: Number(d.pending ?? d.pending_pickups) || 0,
        icon: "bi-hourglass-split"
      },
      {
        title: "Completed",
        value: Number(d.completed ?? d.completed_today) || 0,
        icon: "bi-check-circle"
      },
      {
        title: "Trucks",
        value: Number(d.trucks ?? d.fleet_count) || 0,
        icon: "bi-geo-alt"
      }
    ],
    inventory: Array.isArray(d.inventory) ? d.inventory : [],
    raw: d
  };
}

function normalizeAdmin(res) {
  const d = res?.data ?? {};
  return {
    statItems: [
      {
        title: "Users",
        value: Number(d.users ?? d.total_users) || 0,
        icon: "bi-people"
      },
      {
        title: "Pending",
        value: Number(d.pending_approvals ?? d.pending_users) || 0,
        icon: "bi-person-lines-fill"
      },
      {
        title: "Freight",
        value: Number(d.freight_users) || 0,
        icon: "bi-truck"
      },
      {
        title: "Tickets",
        value: Number(d.tickets ?? d.open_tickets) || 0,
        icon: "bi-ticket-detailed"
      }
    ],
    inventory: Array.isArray(d.inventory) ? d.inventory : [],
    raw: d
  };
}

const ROLE_CONFIG = {
  [ROLES.SELLER]: {
    resolveId: () => getEntityIdForRole(ROLES.SELLER),
    fetch: (id) => getSellerDashboard({ seller_id: id }),
    normalize: normalizeSeller
  },
  [ROLES.BUYER]: {
    resolveId: () => getEntityIdForRole(ROLES.BUYER),
    fetch: (id) => getBuyerDashboard({ buyer_id: id }),
    normalize: normalizeBuyer
  },
  [ROLES.FREIGHT]: {
    resolveId: () => getEntityIdForRole(ROLES.FREIGHT),
    fetch: (id) => getFreightDashboard({ freight_user_id: id }),
    normalize: normalizeFreight
  },
  [ROLES.ADMIN]: {
    resolveId: () => getEntityIdForRole(ROLES.ADMIN),
    fetch: (id) => getAdminDashboard({ admin_id: id }),
    normalize: normalizeAdmin
  }
};

function emptyView() {
  return { statItems: [], inventory: [], raw: {} };
}

/**
 * @returns {Promise<{ role: string, statItems: Array, inventory: Array, raw: object, error: Error|null }>}
 */
export async function loadDashboardForRole() {
  const roleRaw = getRole() || ROLES.SELLER;
  const role = String(roleRaw).toLowerCase();

  const config = ROLE_CONFIG[role];
  if (!config) {
    return { role, ...emptyView(), error: new Error(`No dashboard API for role: ${role}`) };
  }

  const id = config.resolveId();
  if (!id) {
    return {
      role,
      ...emptyView(),
      error: new Error(`Missing entity id for role "${role}" (check login response or roleEntityIds)`)
    };
  }

  try {
    const res = await config.fetch(id);
    return { role, ...config.normalize(res), error: null };
  } catch (err) {
    console.error(`Dashboard API error (${role}):`, err);
    return { role, ...emptyView(), error: err };
  }
}
